import type { TransportMode } from '~/hooks/useTransportState';
import type { VehicleType } from '~/graphql/graphql';

export const CANNED_TALLY_VEHICLE_NAMES = ["48' Flatbed", "73' CENTERBEAM"];

const CANNED_TALLY_VEHICLE_TYPE: Partial<Record<TransportMode, VehicleType>> = {
  railcar: 'DOMESTIC_RAIL',
  truck: 'DOMESTIC_TRUCK',
};

export type VehicleOption = {
  id: string;
  name: string;
  vehicleType: VehicleType;
};

export type SelectOption = {
  label: string;
  value: string;
};

export function transportVehicleOptions(
  vehicles: { id: string; name: string }[]
): SelectOption[] {
  return (vehicles ?? [])
    .map((vehicle) => ({ label: vehicle.name, value: vehicle.id }))
    .sort((left, right) => left.label.localeCompare(right.label));
}

export const VEHICLE_TYPE_ORDER: VehicleType[] = [
  'DOMESTIC_TRUCK',
  'DOMESTIC_RAIL',
  'INTERNATIONAL_CONTAINER',
];

function groupByVehicleType(
  vehicles: VehicleOption[]
): Map<VehicleType, VehicleOption[]> {
  return vehicles.reduce((map, v) => {
    const group = map.get(v.vehicleType) ?? [];
    group.push(v);
    map.set(v.vehicleType, group);
    return map;
  }, new Map<VehicleType, VehicleOption[]>());
}

export function buildVehicleGroups(
  vehicles: VehicleOption[],
  supportedVehicleIds?: string[]
): [VehicleType, VehicleOption[]][] {
  const allVehicleGroups = groupByVehicleType(vehicles);

  const vehicleGroups = supportedVehicleIds
    ? new Map(
        [...allVehicleGroups.entries()]
          .map(
            ([type, groupVehicles]) =>
              [
                type,
                groupVehicles.filter((v: VehicleOption) =>
                  supportedVehicleIds.includes(v.id)
                ),
              ] as [VehicleType, VehicleOption[]]
          )
          .filter(([, groupVehicles]) => groupVehicles.length > 0)
      )
    : allVehicleGroups;

  return Array.from(vehicleGroups.entries())
    .sort(
      ([a], [b]) =>
        VEHICLE_TYPE_ORDER.indexOf(a) - VEHICLE_TYPE_ORDER.indexOf(b)
    )
    .map(
      ([type, groupVehicles]) =>
        [
          type,
          [...groupVehicles].sort((vehicleA, vehicleB) =>
            vehicleA.name.localeCompare(vehicleB.name)
          ),
        ] as [VehicleType, VehicleOption[]]
    );
}

export function getPreferredOrFirstVehicle(
  facilityVehicles: VehicleOption[] | null | undefined,
  preferredVehicleIds: string[] | undefined
): VehicleOption | undefined {
  if (!facilityVehicles || facilityVehicles.length === 0) return undefined;

  const sortedVehicles = [...facilityVehicles].sort((vehicleA, vehicleB) => {
    const typeOrder =
      VEHICLE_TYPE_ORDER.indexOf(vehicleA.vehicleType) -
      VEHICLE_TYPE_ORDER.indexOf(vehicleB.vehicleType);
    if (typeOrder !== 0) return typeOrder;
    return vehicleA.name.localeCompare(vehicleB.name);
  });

  // Prefer a vehicle the product already has a capacity for (so loads show
  // immediately); otherwise fall back to the first facility vehicle, since a
  // missing capacity can now be added inline.
  if (preferredVehicleIds && preferredVehicleIds.length > 0) {
    const preferredVehicle = sortedVehicles.find((vehicle) =>
      preferredVehicleIds.includes(vehicle.id)
    );
    if (preferredVehicle) return preferredVehicle;
  }

  return sortedVehicles[0];
}

export function getContainerVehicles(
  vehicles: VehicleOption[] | null | undefined,
  supportedVehicleIds?: string[]
): VehicleOption[] {
  return buildVehicleGroups(vehicles ?? [], supportedVehicleIds)
    .filter(([type]) => type === 'INTERNATIONAL_CONTAINER')
    .flatMap(([, groupVehicles]) => groupVehicles);
}

export function cannedTallyVehicleChoices(
  vehicles: VehicleOption[],
  plainLineVehicleIds: string[] | undefined,
  tallyLineVehicleIds: string[] | undefined,
  transport: TransportMode | undefined
): { autoSelectIds: string[]; dropdownIds: string[] } {
  const named = vehicles.filter((v) =>
    CANNED_TALLY_VEHICLE_NAMES.includes(v.name)
  );
  const preferred = named
    .filter(
      (v) =>
        plainLineVehicleIds?.includes(v.id) &&
        tallyLineVehicleIds?.includes(v.id)
    )
    .map((v) => v.id);
  if (preferred.length > 0) {
    return { autoSelectIds: preferred, dropdownIds: preferred };
  }
  const vehicleType = transport && CANNED_TALLY_VEHICLE_TYPE[transport];
  return {
    autoSelectIds: named.map((v) => v.id),
    dropdownIds: named
      .filter((v) => !vehicleType || v.vehicleType === vehicleType)
      .map((v) => v.id),
  };
}
